import api from './api';

export interface MediaFile {
  id: string;
  client_id?: string;
  user_id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  media_type: string;
  title?: string;
  description?: string;
  tags: string[];
  folder?: string;
  processing_status: string;
  is_public: boolean;
  file_url?: string;
  thumbnail_url?: string;
  ar_viewer_url?: string;
  created_at: string;
  updated_at: string;
}

export interface MediaListResponse {
  files: MediaFile[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface UploadResponse {
  file: MediaFile;
  message: string;
}

class MediaService {
  async getMediaFiles(
    page = 1, 
    perPage = 20, 
    search?: string, 
    clientId?: string, 
    mediaType?: 'image' | 'video' | 'audio' | 'document' | 'other'
  ): Promise<MediaListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    if (mediaType) {
      params.append('media_type', mediaType);
    }

    const headers: Record<string, string> = {};
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }

    console.log(`Fetching media files with client ID: ${clientId}, media_type: ${mediaType}, params: ${params.toString()}`);
    const response = await api.get(`/media/?${params}`, { headers });
    console.log(`Media API response:`, response.data);
    return response.data;
  }

  async getMediaFile(mediaId: string, clientId?: string): Promise<MediaFile> {
    const headers: Record<string, string> = {};
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    
    const response = await api.get(`/media/${mediaId}`, { headers });
    return response.data;
  }

  async getMediaFilesBatch(mediaIds: string[], clientId?: string): Promise<MediaFile[]> {
    const headers: Record<string, string> = {};
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    
    const response = await api.post('/media/batch', mediaIds, { headers });
    return response.data;
  }

  async uploadFile(file: File, clientId?: string, arViewerUrl?: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (arViewerUrl) {
      formData.append('ar_viewer_url', arViewerUrl);
    }

    console.log(`Uploading: ${file.name} (${this.formatFileSize(file.size)})`);
    
    // The API interceptor will automatically handle FormData Content-Type
    const response = await api.post('/media/upload', formData);
    return response.data;
  }

  async uploadFiles(files: File[], clientId?: string): Promise<UploadResponse[]> {
    const uploads = files.map(file => this.uploadFile(file, clientId));
    return Promise.all(uploads);
  }

  async uploadBrandKitAsset(file: File, clientId?: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_brand_kit_asset', 'true'); // Flag to indicate this is a brand kit asset
    formData.append('tags', 'brand-kit-asset'); // Add special tag for identification

    console.log(`Uploading brand kit asset: ${file.name} (${this.formatFileSize(file.size)})`);
    
    // For now, use the existing upload endpoint with a flag and special tag
    // TODO: Backend should implement separate endpoint to avoid adding to media library
    const response = await api.post('/media/upload', formData);
    return response.data;
  }

  async deleteMediaFile(mediaId: string, clientId?: string): Promise<void> {
    const headers: Record<string, string> = {};
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    
    await api.delete(`/media/${mediaId}`, { headers });
  }

  async updateMediaFile(mediaId: string, data: { filename?: string }, clientId?: string): Promise<MediaFile> {
    const headers: Record<string, string> = {};
    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }
    
    const response = await api.put(`/media/${mediaId}`, data, { headers });
    return response.data;
  }

  getFileTypeIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) {
      return '🖼️';
    } else if (mimeType.startsWith('video/')) {
      return '🎥';
    } else if (mimeType.startsWith('audio/')) {
      return '🎵';
    } else if (mimeType.includes('pdf')) {
      return '📄';
    } else if (mimeType.includes('document') || mimeType.includes('word')) {
      return '📝';
    } else if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return '📊';
    } else {
      return '📁';
    }
  }

  async getMediaStats(): Promise<{ total_files: number; image_count: number; video_count: number }> {
    console.log('Fetching media stats (client ID handled automatically by API service)');
    const response = await api.get('/media/stats');
    console.log('Media stats response:', response.data);
    return response.data;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export default new MediaService();